import { NumInput, Toggle, SumToOneBar } from './primitives.jsx';

const REGION_KEYS = ['eu', 'uk', 'us', 'apac', 'latam', 'mena', 'africa'];
const REGION_LABEL = {
  eu: 'EU',
  uk: 'UK',
  us: 'US',
  apac: 'APAC',
  latam: 'LATAM',
  mena: 'MENA',
  africa: 'Africa',
};

export default function RegionsSection({ regions, regionalMix, set }) {
  return (
    <div className="space-y-5">
      <RegionsTable regions={regions} set={set} />
      <RegionalMixTable mix={regionalMix} set={set} />
    </div>
  );
}

function RegionsTable({ regions, set }) {
  return (
    <div className="rounded-box border border-carbon-100 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-carbon-100">
        <p className="label text-carbon-800">Per-region inputs</p>
        <p className="caption text-carbon-400 mt-1">
          Card revenue / FX behavior / CAC time-series / KYC opt-in flag
        </p>
      </div>
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-left">
          <thead className="bg-carbon-50">
            <tr className="caption text-carbon-500">
              <th className="px-3 py-2">Region</th>
              <th className="px-3 py-2 text-right">Card rate</th>
              <th className="px-3 py-2 text-right">FX usage</th>
              <th className="px-3 py-2 text-right">FX vol/mo ($)</th>
              <th className="px-3 py-2 text-right">CAC Y1</th>
              <th className="px-3 py-2 text-right">CAC Y2</th>
              <th className="px-3 py-2 text-right">CAC Y3</th>
              <th className="px-3 py-2 text-center">KYC opt-in</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carbon-100">
            {REGION_KEYS.map((rk) => {
              const r = regions[rk];
              const setR = (field, value) => set(`regions.${rk}.${field}`, value);
              return (
                <tr key={rk}>
                  <td className="px-3 py-2 label text-carbon-800">{REGION_LABEL[rk]}</td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.interchangeRate}
                      pct
                      step={0.001}
                      decimals={2}
                      onChange={(v) => setR('interchangeRate', v)}
                      ariaLabel={`${rk} card revenue rate`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.fxUsageRate}
                      pct
                      step={0.01}
                      onChange={(v) => setR('fxUsageRate', v)}
                      ariaLabel={`${rk} FX usage`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.fxAvgVolumeMo}
                      step={50}
                      onChange={(v) => setR('fxAvgVolumeMo', v)}
                      ariaLabel={`${rk} FX volume`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.cacBlendedY1}
                      step={1}
                      onChange={(v) => setR('cacBlendedY1', v)}
                      ariaLabel={`${rk} CAC Y1`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.cacBlendedY2}
                      step={1}
                      onChange={(v) => setR('cacBlendedY2', v)}
                      ariaLabel={`${rk} CAC Y2`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <NumInput
                      value={r.cacBlendedY3}
                      step={1}
                      onChange={(v) => setR('cacBlendedY3', v)}
                      ariaLabel={`${rk} CAC Y3`}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Toggle
                      checked={r.kycRequiresExplicitActivation}
                      onChange={(v) => setR('kycRequiresExplicitActivation', v)}
                      ariaLabel={`${rk} KYC opt-in`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegionalMixTable({ mix, set }) {
  return (
    <div className="rounded-box border border-carbon-100 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-carbon-100 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="label text-carbon-800">Regional mix · share of net-new users by year</p>
          <p className="caption text-carbon-400 mt-1">Each row sums to 1.0</p>
        </div>
      </div>
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-left">
          <thead className="bg-carbon-50">
            <tr className="caption text-carbon-500">
              <th className="px-3 py-2">Year</th>
              {REGION_KEYS.map((rk) => (
                <th key={rk} className="px-3 py-2 text-right">
                  {REGION_LABEL[rk]}
                </th>
              ))}
              <th className="px-3 py-2 text-right">Sum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carbon-100">
            {['y1', 'y2', 'y3'].map((y) => {
              const yMix = mix[y];
              return (
                <tr key={y}>
                  <td className="px-3 py-2 label text-carbon-800 uppercase">{y}</td>
                  {REGION_KEYS.map((rk) => (
                    <td key={rk} className="px-3 py-2 text-right">
                      <NumInput
                        value={yMix[rk]}
                        pct
                        step={0.005}
                        onChange={(v) => set(`regionalMix.${y}.${rk}`, v)}
                        ariaLabel={`${y} ${rk} share`}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <SumToOneBar values={yMix} label="" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
